import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type { UserAdminView } from '@/types/admin';

/**
 * Fetches the super-admin detail view for a single user via the
 * `get_user_admin_view(uuid)` RPC. Returns null while disabled (no userId).
 *
 * The RPC is super-admin gated (RAISEs for non-staff). Callers should only
 * mount this hook from inside the staff-only AdminBar surfaces.
 */
export function useUserAdminView(userId: string | null) {
  return useQuery<UserAdminView | null>({
    queryKey: ['admin', 'user-view', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase.rpc('get_user_admin_view', {
        p_user_id: userId,
      });
      if (error) {
        captureError(error as Error, { feature: 'admin-user-view' });
        throw error;
      }
      // Telemetry for a real RPC regression where get_user_admin_view stops
      // returning account_association. The render falls back gracefully
      // (card hidden) but without this log a server-side regression would
      // be invisible — admins just see a missing card and assume it's
      // expected.
      if (data && typeof data === 'object' && !('account_association' in data)) {
        captureError(
          new Error('get_user_admin_view returned response without account_association'),
          { feature: 'admin-user-view-shape' },
        );
      }
      return data as UserAdminView;
    },
    enabled: !!userId,
    // Admin-sensitive data (last_sign_in_at, current workspace membership)
    // must always be considered stale so it refetches on focus/mount.
    staleTime: 0,
    gcTime: 30_000,
  });
}
